import type {ReactNode} from 'react';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Group, Stack, Text} from '@mantine/core';
import {useDebouncedValue} from '@mantine/hooks';
import {IconChevronsDown, IconChevronsUp} from '@tabler/icons-react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Spinner} from 'src/design/atoms/Spinner';
import {Textbox} from 'src/design/atoms/Textbox';
import type {HierarchyTreeNode} from 'src/design/organisms/HierarchyTree';
import {HierarchyTree} from 'src/design/organisms/HierarchyTree';
import {
  collectHierarchyIds,
  filterHierarchyNodes,
} from 'src/design/organisms/hierarchyTreeUtils';

// Skips the full-tree render when the pane re-renders with unchanged tree props.
const MemoHierarchyTree = memo(HierarchyTree);

export interface TreeBrowserPaneProps {
  nodes: HierarchyTreeNode[];
  selectedId?: string;
  onSelect: (id: string) => void;
  expandedIds?: string[];
  onExpandedChange?: (ids: string[]) => void;
  defaultExpandedIds?: string[];
  /** Small uppercase dimmed heading above the pane content. */
  title?: string;
  header?: ReactNode;
  footer?: ReactNode;
  searchable?: boolean;
  search?: string;
  onSearchChange?: (search: string) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  error?: ReactNode;
  emptyText?: string;
}

/**
 * Sidebar pane for a two-pane browser: title row with expand/collapse-all, header/footer
 * slots, debounced ancestor-preserving search, a scrollable tree body and loading/empty states.
 * @registry Tree navigation pane with search, expand/collapse all, scrolling tree body and header/footer slots — pane half of `BrowserLayout`.
 */
export const TreeBrowserPane = ({
  nodes,
  selectedId,
  onSelect,
  expandedIds,
  onExpandedChange,
  defaultExpandedIds,
  title,
  header,
  footer,
  searchable = false,
  search: controlledSearch,
  onSearchChange,
  searchPlaceholder,
  isLoading,
  error,
  emptyText,
}: TreeBrowserPaneProps) => {
  const [localSearch, setLocalSearch] = useState('');
  const search = controlledSearch ?? localSearch;
  const setSearch = onSearchChange ?? setLocalSearch;
  const [localExpandedIds, setLocalExpandedIds] = useState<string[]>(
    defaultExpandedIds ?? []
  );
  const expanded = expandedIds ?? localExpandedIds;
  const setExpanded = onExpandedChange ?? setLocalExpandedIds;
  const [debouncedSearch] = useDebouncedValue(search, 100);
  const term = debouncedSearch.trim().toLowerCase();
  const bodyRef = useRef<HTMLDivElement>(null);

  const pendingScrollId = useRef<string>();

  useEffect(() => {
    pendingScrollId.current = selectedId;
  }, [selectedId]);

  // One scroll per selection, retried until its ancestors expand and the row mounts;
  // later expand/collapse must not pull the body back. jsdom has no scrollIntoView.
  useEffect(() => {
    if (!selectedId || pendingScrollId.current !== selectedId) return;
    const rows = bodyRef.current?.querySelectorAll('[role="treeitem"]') ?? [];
    const row = [...rows].find(
      (candidate): candidate is HTMLElement =>
        candidate instanceof HTMLElement &&
        candidate.dataset.value === selectedId
    );
    if (!row) return;
    pendingScrollId.current = undefined;
    if (row.scrollIntoView) row.scrollIntoView({block: 'nearest'});
  }, [selectedId, nodes, expanded]);

  const filtered = useMemo(
    () => (term ? filterHierarchyNodes(nodes, term) : nodes),
    [nodes, term]
  );
  const searchExpandedIds = useMemo(
    () => (term ? collectHierarchyIds(filtered) : undefined),
    [filtered, term]
  );
  const allIds = useMemo(() => collectHierarchyIds(nodes), [nodes]);

  const showTree = !error && !isLoading && nodes.length > 0;
  // Every id past the top level is a descendant, so a longer list means something expands.
  const showExpandActions = showTree && !term && allIds.length > nodes.length;

  const renderBody = () => {
    if (error) return error;
    if (isLoading) return <Spinner />;
    if (nodes.length === 0)
      return emptyText ? (
        <Text size="sm" c="dimmed">
          {emptyText}
        </Text>
      ) : null;
    if (filtered.length === 0)
      return (
        <Text size="sm" c="dimmed">
          No matching items.
        </Text>
      );

    // Re-mounted per search term: `defaultExpandedIds` only applies on the initial render.
    return (
      <MemoHierarchyTree
        key={term}
        nodes={filtered}
        selectedId={selectedId}
        onSelect={onSelect}
        defaultExpandedIds={searchExpandedIds ?? defaultExpandedIds}
        expandedIds={term ? undefined : expanded}
        onExpandedChange={term ? undefined : setExpanded}
      />
    );
  };

  return (
    <Stack gap="sm" h="100%" mih={0}>
      {(title || showExpandActions) && (
        <Group justify="space-between" wrap="nowrap" gap="xs" mih={36}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
            {title}
          </Text>
          {showExpandActions && (
            <Group gap={2} wrap="nowrap">
              <ActionIcon
                tooltipText="Expand all"
                onClick={() => setExpanded(allIds)}>
                <IconChevronsDown size={16} />
              </ActionIcon>
              <ActionIcon
                tooltipText="Collapse all"
                onClick={() =>
                  setExpanded(nodes.length === 1 ? [nodes[0].id] : [])
                }>
                <IconChevronsUp size={16} />
              </ActionIcon>
            </Group>
          )}
        </Group>
      )}

      {searchable && (
        <Textbox
          ariaLabel={searchPlaceholder ?? 'Search tree'}
          placeholder={searchPlaceholder}
          value={search}
          onChangeCallback={event => setSearch(event.currentTarget.value)}
        />
      )}

      {header}

      {/* Native overflow: ScrollArea's 100%-height viewport never resolves under a max-height ancestor. */}
      <Box
        ref={bodyRef}
        flex="1 1 auto"
        mih={0}
        pr="xs"
        style={{overflowY: 'auto'}}
        data-testid="tree-browser-pane-body">
        {renderBody()}
      </Box>

      {footer && <Box mt="auto">{footer}</Box>}
    </Stack>
  );
};
